import { IconArrowRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";

import type { Capacity, Workspace } from "../../api/types";
import { gpuCountLabel, workloadFamilyLabel } from "../../lib/capacity";
import { formatRemaining } from "../../lib/format";
import { waitingCardLine } from "../../lib/waiting";
import {
  isIdleAtRisk,
  workspaceHeadline,
  workspaceStateLabel,
  workspaceTone,
} from "../../lib/workload";
import { ConnectionBlock } from "./ConnectionBlock";
import { WorkspaceActions } from "./WorkspaceActions";

interface WorkspaceCardProps {
  workspace: Workspace;
  /** The measured fleet, when it has been read: it says whether naming the
   * GPU family means anything on this deployment. */
  capacity?: Capacity | undefined;
}

/**
 * One of your workspaces, summarised on the home screen.
 *
 * Several can be on screen at once — a researcher may hold several workspaces
 * (`docs/API.md`) — so the title carries the size and the heading id carries
 * the workspace id: two cards both called "Your workspace" would be
 * indistinguishable to everyone, screen readers first.
 *
 * Args:
 *     workspace: One of your live workspaces.
 *     capacity: The measured fleet, if it has been read.
 *
 * Returns:
 *     A card with the state, how to connect and the two actions.
 */
export function WorkspaceCard({
  workspace,
  capacity,
}: WorkspaceCardProps): JSX.Element {
  const lastEvent = workspace.events[workspace.events.length - 1];
  const tone = workspaceTone(workspace);
  const headingId = `workspace-${workspace.id}-title`;
  // Which GPU this workspace holds, on a fleet that has more than one kind:
  // the choice was made once, at creation, and is fixed for its whole life —
  // a screen that never repeats it leaves the researcher no way to check it.
  const family = workloadFamilyLabel(capacity, workspace.gpu_family);
  // A ready workspace on an idle countdown is exactly the one whose last event
  // matters most: it says how long is left before the console takes the GPUs
  // back. Hiding it behind the connection block is how it gets missed.
  const showEvent = !workspace.connection?.ready || isIdleAtRisk(workspace);

  return (
    <section
      className="workspace-card"
      data-tone={tone}
      aria-labelledby={headingId}
    >
      <div className="workspace-card__head">
        <h2 className="workspace-card__title" id={headingId}>
          Your {workspace.gpus}-GPU {family ? `${family} ` : ""}workspace
        </h2>
        <span className="badge" data-tone={tone}>
          <span className="badge__dot" aria-hidden="true" />
          {workspaceStateLabel(workspace)}
        </span>
      </div>

      <p className="workspace-card__headline">{workspaceHeadline(workspace)}</p>

      {/* Where it stands, in one line — the clause after the place is the
          console's own, and nothing here rewrites it. */}
      {workspace.waiting ? (
        <p className="workspace-card__queue">
          {waitingCardLine(workspace.waiting)}
        </p>
      ) : null}

      <p className="workspace-card__meta">
        {gpuCountLabel(workspace.gpus, family)}
        {workspace.memory_gb > 0 ? ` · ${workspace.memory_gb} GiB` : ""} ·{" "}
        {workspace.image}
        {workspace.node ? (
          <>
            {" · "}
            <span className="mono">{workspace.node}</span>
          </>
        ) : null}
        {workspace.expires_at ? (
          <> · {formatRemaining(workspace.expires_at)} left</>
        ) : null}
      </p>

      {showEvent && lastEvent ? (
        <p className="workspace-card__event">{lastEvent.message}</p>
      ) : null}

      {workspace.connection?.ready ? (
        <ConnectionBlock connection={workspace.connection} compact />
      ) : null}

      <div className="workspace-card__foot">
        <WorkspaceActions workspace={workspace} />
        <Link className="btn btn--ghost" to={`/workspaces/${workspace.id}`}>
          Details
          <IconArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}
